// base-chart.component.ts
import {
  Component,
  Input,
  OnInit,
  ElementRef,
  OnDestroy,
  OnChanges,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import HC_stock from 'highcharts/modules/stock';
HC_stock(Highcharts);
import { ChartConfigService } from '../services/chart-config.service';

@Component({
  selector: 'app-base-chart',
  template: '<div class="chart-container"></div>',
})
export abstract class BaseHighChartComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() chartData: Highcharts.SeriesOptionsType[] = [];
  @Input() colorPalette: string[] = [];
  @Input() height: number | string = 'auto';
  @Input() useStockChart: boolean = true;

  protected chart: Highcharts.Chart | undefined;
  private chartId: string;

  constructor(
    protected elementRef: ElementRef,
    protected configService: ChartConfigService,
  ) {
    this.chartId = `chart-${Math.random().toString(36).substring(7)}`;
  }

  ngOnInit() {
    this.createChart();
  }

  ngOnChanges() {
    if (this.chart) {
      this.chart.update(this.getChartConfig(), true);
    } else if (this.chartData?.length > 0) {
      this.createChart();
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  /**
   * Triggers a reflow of the chart to recalculate dimensions.
   * Call this after container size changes (e.g., window resize, sidebar toggle).
   */
  public reflow(): void {
    this.chart?.reflow();
  }

  /**
   * Returns whether the chart instance exists and is ready
   */
  public isChartReady(): boolean {
    return !!this.chart;
  }

  protected abstract getChartConfig(): Highcharts.Options;

  protected createChart() {
    if (!this.chartData || this.chartData.length === 0) {
      console.warn('Chart data is not provided.');
      this.elementRef.nativeElement.querySelector(
        '.chart-container',
      ).innerHTML = 'No data to display';
      return;
    }

    const config = this.getChartConfig();

    const container =
      this.elementRef.nativeElement.querySelector('.chart-container');
    container.id = this.chartId;

    if (this.useStockChart) {
      this.chart = Highcharts.stockChart(this.chartId, config);
    } else {
      this.chart = Highcharts.chart(this.chartId, config);
    }
  }
}
